import { CircleAlert, Inbox, Loader2 } from "lucide-react";
import type { ReactNode } from "react";
import { Botao } from "./botao";
import { cn } from "./cn";

/**
 * Os três estados que toda tela com dado remoto precisa e que quase sempre faltam:
 * carregando, erro e vazio.
 *
 * Centralizados aqui porque a alternativa observada é cada tela improvisar o seu —
 * uma mostra "Carregando...", outra um spinner, uma terceira nada, e a tela em erro
 * fica em branco sem explicação para o usuário.
 */

export function EstadoCarregando({
  rotulo = "Carregando",
  className,
}: {
  readonly rotulo?: string;
  readonly className?: string | undefined;
}) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "flex items-center justify-center gap-3 rounded-lg border border-dashed bg-card/50 p-8 text-sm text-muted-foreground",
        className,
      )}
    >
      <Loader2 aria-hidden="true" className="size-4 animate-spin text-primary" />
      {rotulo}
    </div>
  );
}

/** Esqueleto para carregamento de conteúdo já posicionado (tabela, cartão). */
export function Esqueleto({ className }: { readonly className?: string | undefined }) {
  return (
    <div
      aria-hidden="true"
      className={cn("animate-pulse rounded-md bg-muted", className)}
    />
  );
}

export type PropsDoEstadoDeErro = {
  readonly titulo?: string;
  readonly mensagem: string;
  /** Código de correlação (requestId) para o usuário citar no suporte. */
  readonly codigo?: string | undefined;
  readonly aoTentarNovamente?: (() => void) | undefined;
  readonly className?: string | undefined;
};

export function EstadoDeErro({
  titulo = "Não foi possível carregar",
  mensagem,
  codigo,
  aoTentarNovamente,
  className,
}: PropsDoEstadoDeErro) {
  return (
    <div
      role="alert"
      className={cn(
        "animate-aparecer rounded-lg border border-destructive/30 bg-destructive/5 p-5",
        className,
      )}
    >
      <div className="flex items-start gap-3">
        <CircleAlert
          aria-hidden="true"
          className="mt-0.5 size-5 shrink-0 text-destructive"
        />
        <div className="min-w-0 space-y-1">
          <p className="font-semibold text-destructive">{titulo}</p>
          <p className="text-sm text-foreground">{mensagem}</p>
          {codigo ? (
            <p className="text-xs text-muted-foreground">
              Código para o suporte:{" "}
              <code className="rounded bg-muted px-1 py-0.5 font-mono">{codigo}</code>
            </p>
          ) : null}
          {aoTentarNovamente ? (
            <Botao
              variante="contorno"
              tamanho="pequeno"
              className="mt-3"
              onClick={aoTentarNovamente}
            >
              Tentar novamente
            </Botao>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export type PropsDoEstadoVazio = {
  readonly titulo: string;
  readonly descricao?: string | undefined;
  readonly acao?: ReactNode;
  /** Ícone do lucide. O padrão serve para lista vazia. */
  readonly icone?: ReactNode;
  readonly className?: string | undefined;
};

export function EstadoVazio({
  titulo,
  descricao,
  acao,
  icone,
  className,
}: PropsDoEstadoVazio) {
  return (
    <div
      className={cn(
        "animate-aparecer rounded-lg border border-dashed bg-card/50 px-6 py-10 text-center",
        className,
      )}
    >
      <div className="mx-auto mb-3 flex size-10 items-center justify-center rounded-full bg-muted text-muted-foreground [&_svg]:size-5">
        {icone ?? <Inbox aria-hidden="true" />}
      </div>
      <p className="font-semibold">{titulo}</p>
      {descricao ? (
        <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
          {descricao}
        </p>
      ) : null}
      {acao ? (
        <div className="mt-6 flex flex-col items-center justify-center gap-3">
          {acao}
        </div>
      ) : null}
    </div>
  );
}
